import {
    createContext,
    createEffect,
    createSignal,
    onMount,
    useContext,
    type JSX,
    type Accessor,
} from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useApi, isOk } from '../api';
import { useVisibilityPoll } from '../hooks/useVisibilityPoll';
import { useUserContext } from '../components/UserContextProvider';
import { useRoleParser } from '../hooks/permissions';
import type { BotRegistryEntry } from '../components/BotSelector';
import type { BotGroupDto } from '../api/generated/schemas';
import { can } from '../services/resourceActions';

interface ActiveBotState {
    botId: Accessor<string | null>;
    bot: Accessor<BotRegistryEntry | null>;
    bots: Accessor<readonly BotRegistryEntry[]>;
    canViewAll: Accessor<boolean>;
    groups: Accessor<readonly BotGroupDto[]>;
    groupId: Accessor<string | null>;
    group: Accessor<BotGroupDto | null>;
    groupBots: Accessor<BotRegistryEntry[]>;
    botsInGroup: (memberIds: readonly string[]) => BotRegistryEntry[];
    targetIds: (action: string) => string[];
    setBot: (id: string) => void;
    selectGroup: (id: string) => void;
    setFocus: (id: string) => void;
    clear: () => void;
    refresh: () => Promise<void>;
}

const STORAGE_KEY = 'vektor.activeBot';
const POLL_MS = 5000;

interface PersistedState {
    botId: string | null;
    groupId: string | null;
}

function readPersisted(): PersistedState {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { botId: null, groupId: null };
        const parsed = JSON.parse(raw);
        const botId = typeof parsed?.botId === 'string' ? parsed.botId : null;
        const groupId = typeof parsed?.groupId === 'string' ? parsed.groupId : null;
        return { botId, groupId };
    } catch {
        return { botId: null, groupId: null };
    }
}

function writePersisted(state: PersistedState) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* quota */ }
}

const ActiveBotContext = createContext<ActiveBotState>();

export function ActiveBotProvider(props: { children: JSX.Element }) {
    const api = useApi();
    const { user } = useUserContext();
    const { parseRoles, roles } = useRoleParser();
    const isRoot = () => (parseRoles(user()?.permissions ?? 0, roles))[roles.indexOf('Root')];
    // The admin-center setting is the single source of truth for "browse as admin": no separate
    // per-session toggle on top of it, so turning it off in the admin center always means the
    // member app shows only your own bots, and turning it on always shows every bot.
    const canViewAll = () => isRoot() && user()?.showAdminUiInMemberApp === true;

    const initial = readPersisted();
    const [botId, setBotIdSig] = createSignal<string | null>(initial.botId);
    const [groupId, setGroupIdSig] = createSignal<string | null>(initial.groupId);
    const [bots, setBots] = createStore<BotRegistryEntry[]>([]);
    const [groups, setGroups] = createStore<BotGroupDto[]>([]);

    const persist = () => writePersisted({ botId: botId(), groupId: groupId() });

    const group = (): BotGroupDto | null => {
        const id = groupId();
        if (!id) return null;
        return groups.find(g => g.id === id) ?? null;
    };

    const botsInGroup = (memberIds: readonly string[]): BotRegistryEntry[] =>
        memberIds
            .map(memberId => bots.find(b => b.id === memberId))
            .filter((b): b is BotRegistryEntry => !!b);

    const groupBots = (): BotRegistryEntry[] => botsInGroup(group()?.botIds ?? []);

    const focusFirstMemberIfOutside = () => {
        const members = groupBots();
        if (members.length === 0 || members.some(b => b.id === botId())) return;
        setBotIdSig(members[0].id);
    };

    const refreshBots = async () => {
        const res = canViewAll()
            ? await api.botRegistry.listBots({ all: true })
            : await api.botRegistry.listBots();
        if (!isOk(res.data)) return;
        const list = res.data.value.bots as unknown as BotRegistryEntry[];
        setBots(reconcile(list, { key: 'id' }));

        const current = botId();
        if (current && !list.find(b => b.id === current)) setBotIdSig(null);
        if (!botId() && list.length > 0) setBotIdSig(list[0].id);
        focusFirstMemberIfOutside();
        persist();
    };

    const refreshGroups = async () => {
        const res = await api.botGroup.listBotGroups();
        if (!isOk(res.data)) return;
        setGroups(reconcile(res.data.value.groups, { key: 'id' }));
        if (groupId() && !group()) setGroupIdSig(null);
        focusFirstMemberIfOutside();
        persist();
    };

    // Two independent loads: a failing group request must never hold back the bot list every page reads.
    const refresh = async () => {
        if (!user()?.loggedin) return;
        await Promise.all([refreshBots(), refreshGroups()]);
    };

    const setBot = (id: string) => {
        setGroupIdSig(null);
        setBotIdSig(id);
        persist();
    };

    const selectGroup = (id: string) => {
        setGroupIdSig(id);
        focusFirstMemberIfOutside();
        persist();
    };

    const setFocus = (id: string) => {
        setBotIdSig(id);
        persist();
    };

    // A lone bot goes through unfiltered so the server stays the judge; group members lacking the action are dropped.
    const targetIds = (action: string): string[] => {
        if (!group()) {
            const id = botId();
            return id ? [id] : [];
        }
        return groupBots().filter(b => can(b.myActions, action)).map(b => b.id);
    };

    const clear = () => {
        setGroupIdSig(null);
        setBotIdSig(null);
        persist();
    };

    const bot = (): BotRegistryEntry | null => {
        const id = botId();
        if (!id) return null;
        return bots.find(b => b.id === id) ?? null;
    };

    createEffect(() => {
        if (user()?.loggedin) void refresh();
    });

    onMount(() => {
        useVisibilityPoll(() => void refresh(), POLL_MS);
    });

    const state: ActiveBotState = {
        botId, bot,
        bots: () => bots,
        canViewAll,
        groups: () => groups,
        groupId, group, groupBots, botsInGroup, targetIds,
        setBot, selectGroup, setFocus, clear, refresh,
    };

    return (
        <ActiveBotContext.Provider value={state}>
            {props.children}
        </ActiveBotContext.Provider>
    );
}

export function useActiveBot(): ActiveBotState {
    const ctx = useContext(ActiveBotContext);
    if (!ctx) throw new Error('useActiveBot must be used within ActiveBotProvider');
    return ctx;
}
